<template>
  <section class="p-6 flex flex-col gap-6">
    <!-- Breadcrumb -->
    <nav class="text-gray-600 text-sm">
      <span class="font-semibold cursor-pointer" @click="router.push('/')">Home</span>
      <span class="mx-2">/</span>
      <span class="font-semibold cursor-pointer" @click="router.push({ name: 'Partners' })">Partners</span>
      <span class="mx-2">/</span>
      <span class="text-gray-800 font-bold">Edit Partner</span>
    </nav>

    <h3 class="font-bold text-2xl">Edit Partner #{{ partnerId }}</h3>

    <!-- Errors -->
    <div
      v-if="errors.length"
      class="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md text-sm"
    >
      <ul class="list-disc list-inside">
        <li v-for="(err, idx) in errors" :key="idx">{{ err }}</li>
      </ul>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-gray-500 text-sm">Loading partner data...</div>

    <!-- Form -->
    <form
      v-else
      @submit.prevent="submitForm"
      class="bg-white shadow rounded-lg p-6 flex flex-col gap-6"
    >
      <!-- Name & Website -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium mb-1">Partner Name</label>
          <input
            v-model="form.name"
            type="text"
            class="border border-gray-300 rounded-md w-full px-4 py-2"
            placeholder="Ex: Oman Arab Bank"
          />
        </div>

        <div>
          <label class="block text-sm font-medium mb-1">Website URL</label>
          <input
            v-model="form.website_url"
            type="url"
            class="border border-gray-300 rounded-md w-full px-4 py-2"
            placeholder="https://www.example.com"
          />
        </div>
      </div>

      <!-- Sort Order & Logo -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium mb-1">Sort Order</label>
          <input
            v-model.number="form.sort_order"
            type="number"
            min="0"
            class="border border-gray-300 rounded-md w-full px-4 py-2"
            placeholder="1"
          />
        </div>

        <div>
          <label class="block text-sm font-medium mb-1">Logo</label>
          <input
            type="file"
            accept="image/*"
            @change="onLogoChange"
            class="border border-gray-300 rounded-md w-full px-4 py-2 bg-white"
          />
          <div v-if="logoPreview" class="mt-2">
            <p class="text-xs text-gray-600 mb-1">Preview:</p>
            <img
              :src="logoPreview"
              alt="Logo preview"
              class="w-16 h-16 object-contain border rounded-md"
            />
          </div>
          <div v-else-if="form.logo_url" class="mt-2">
            <p class="text-xs text-gray-600 mb-1">Current logo:</p>
            <img
              :src="form.logo_url"
              :alt="form.name"
              class="w-16 h-16 object-contain border rounded-md"
            />
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex justify-end gap-3 mt-4">
        <button
          type="button"
          class="px-4 py-2 border rounded-md"
          @click="router.push({ name: 'Partners' })"
          :disabled="isSubmitting"
        >
          Cancel
        </button>
        <button
          type="submit"
          class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60"
          :disabled="isSubmitting"
        >
          <span v-if="isSubmitting">Saving...</span>
          <span v-else>Save Changes</span>
        </button>
      </div>
    </form>
  </section>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import apiClient from "@/apiClient";
import { useToast } from "vue-toastification";

const router = useRouter();
const route = useRoute();
const toast = useToast();

const partnerId = ref(route.params.id);

const form = ref({
  name: "",
  website_url: "",
  sort_order: 0,
  logo_url: null,
});

const logoFile = ref(null);
const logoPreview = ref(null);
const loading = ref(false);
const errors = ref([]);
const isSubmitting = ref(false);

function onLogoChange(event) {
  const file = event.target.files[0];
  if (!file) {
    logoFile.value = null;
    logoPreview.value = null;
    return;
  }
  logoFile.value = file;
  logoPreview.value = URL.createObjectURL(file);
}

async function fetchPartner() {
  try {
    loading.value = true;
    const res = await apiClient.get(`/partners/${partnerId.value}`);
    const payload = res.data?.data?.partners || [];
    const partner = payload.find((p) => p.id == partnerId.value);

    if (!partner) {
      errors.value.push("Partner not found.");
      return;
    }

    form.value = {
      name: partner.name,
      website_url: partner.website_url,
      sort_order: partner.sort_order,
      logo_url: partner.logo_url,
    };
  } catch (error) {
    console.error("Error fetching partner:", error);
    errors.value.push("Failed to load partner data.");
  } finally {
    loading.value = false;
  }
}

function validateForm() {
  errors.value = [];

  if (!form.value.name.trim()) errors.value.push("Partner name is required.");
  if (
    form.value.website_url &&
    !/^https?:\/\/.+\..+/.test(form.value.website_url)
  )
    errors.value.push("Invalid website URL.");

  return errors.value.length === 0;
}

async function submitForm() {
  try {
    if (!validateForm()) return;
    isSubmitting.value = true;

    const fd = new FormData();
    fd.append("name", form.value.name);
    if (form.value.website_url) fd.append("website_url", form.value.website_url);
    fd.append("sort_order", form.value.sort_order ?? 0);
    if (logoFile.value) fd.append("logo", logoFile.value);

    const res = await apiClient.post(`/partners/${partnerId.value}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    toast.success("Partner updated successfully!");
    router.push({ name: "Partners" });
  } catch (error) {
    console.error("Error updating partner:", error);
    errors.value = [];

    if (error.response?.data) {
      const data = error.response.data;
      if (data.message && typeof data.message === "string")
        errors.value.push(data.message);

      if (data.errors)
        Object.values(data.errors).forEach((msgArr) =>
          msgArr.forEach((msg) => errors.value.push(msg))
        );
    } else {
      errors.value.push("Failed to update partner. Please try again.");
    }
  } finally {
    isSubmitting.value = false;
  }
}

onMounted(() => {
  fetchPartner();
});
</script>

<style scoped>
</style>
